var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import global from '../shim/global';
import has from './has';
import WeakMap from '../shim/WeakMap';
import Set from '../shim/Set';
import Map from '../shim/Map';
import { flat } from '../shim/array';
import { Registry, isWidget, isWidgetBaseConstructor, isWidgetFunction, isWNodeFactory } from './Registry';
import { auto } from './diff';
import RegistryHandler from './RegistryHandler';
const defaultNodeApi = {
    getDocument() {
        return global.document;
    },
    getBody() {
        return global.document.body;
    },
    getHead() {
        return global.document.head;
    },
    getTag(domNode = { tagName: '' }) {
        return domNode.tagName;
    },
    create(tag) {
        return global.document.createElement(tag);
    },
    createText(text) {
        return global.document.createTextNode(text);
    },
    createWithNamespace(tag, namespace) {
        return global.document.createElementNS(tag, namespace);
    },
    getProperty(domNode, key) {
        return domNode[key];
    },
    setProperty(domNode, key, value) {
        domNode[key] = value;
    },
    getAttribute(domNode, key) {
        return domNode.getAttribute(key);
    },
    setAttribute(domNode, key, value) {
        domNode.setAttribute(key, value);
    },
    setAttributeWithNamespace(domNode, namespace, key, value) {
        domNode.setAttributeNS(namespace, key, value);
    },
    removeAttribute(domNode, key) {
        domNode.removeAttribute(key);
    },
    setStyle(domNode, key, value) {
        domNode.style[key] = value || '';
    },
    insertBefore(domNode, newNode, referenceNode) {
        domNode.insertBefore(newNode, referenceNode);
    },
    removeChild(domNode, childNode) {
        domNode.removeChild(childNode);
    },
    getChildren(domNode) {
        return domNode.childNodes;
    },
    addEvent(domNode, name, callback, options) {
        has('dom-passive-event')
            ? domNode.addEventListener(name, callback, options)
            : domNode.addEventListener(name, callback);
    },
    removeEvent(domNode, name, callback) {
        domNode.removeEventListener(name, callback);
    },
    getParent(domNode) {
        return domNode.parentNode;
    },
    replaceChild(domNode, newChild, oldChild) {
        domNode.replaceChild(newChild, oldChild);
    },
    contains(domNode, child) {
        return domNode.contains(child);
    },
    requestAnimationFrame(callback) {
        return global.requestAnimationFrame(callback);
    },
    cancelAnimationFrame(number) {
        return global.cancelAnimationFrame(number);
    },
    callProperty(domNode, property) {
        typeof domNode[property] === 'function' && domNode[property]();
    },
    isTextNode(domNode) {
        return !!(domNode && domNode.nodeType === 3);
    },
    equals(nodeA, nodeB) {
        return nodeA === nodeB;
    }
};
const EMPTY_ARRAY = [];
const nodeOperations = ['focus', 'blur', 'scrollIntoView', 'click'];
const NAMESPACE_W3 = 'http://www.w3.org/';
const NAMESPACE_SVG = NAMESPACE_W3 + '2000/svg';
const NAMESPACE_XLINK = NAMESPACE_W3 + '1999/xlink';
const WNODE = '__WNODE_TYPE';
const VNODE = '__VNODE_TYPE';
const DOMVNODE = '__DOMVNODE_TYPE';
// @ts-ignore
const scope = typeof __DOJO_SCOPE === 'string' ? __DOJO_SCOPE : 'dojo_scope';
if (!global[scope]) {
    global[scope] = {};
}
export function setRendering(value) {
    global[scope].rendering = value;
}
export function incrementBlockCount() {
    const blocksPending = global[scope].blocksPending || 0;
    global[scope].blocksPending = blocksPending + 1;
}
export function decrementBlockCount() {
    const blocksPending = global[scope].blocksPending || 0;
    global[scope].blocksPending = blocksPending - 1;
}
export function isTextNode(item) {
    return item && item.nodeType === 3;
}
function isLazyDefine(item) {
    return Boolean(item && item.label);
}
function isWNodeWrapper(child) {
    return child && isWNode(child.node);
}
function isVNodeWrapper(child) {
    return !!child && isVNode(child.node);
}
function isVirtualWrapper(child) {
    return isVNodeWrapper(child) && child.node.tag === 'virtual';
}
function isBodyWrapper(wrapper) {
    return isVNodeWrapper(wrapper) && wrapper.node.tag === 'body';
}
function isHeadWrapper(wrapper) {
    return isVNodeWrapper(wrapper) && wrapper.node.tag === 'head';
}
function isSpecialWrapper(wrapper) {
    return isHeadWrapper(wrapper) || isBodyWrapper(wrapper) || isVirtualWrapper(wrapper);
}
function isAttachApplication(value) {
    return !!value.type;
}
export function isWNode(child) {
    return Boolean(child && child !== true && typeof child !== 'string' && child.type === WNODE);
}
export function isVNode(child) {
    return Boolean(child &&
        child !== true &&
        typeof child !== 'number' &&
        typeof child !== 'string' &&
        (child.type === VNODE || child.type === DOMVNODE));
}
export function isDomVNode(child) {
    return Boolean(child && child !== true && typeof child !== 'number' && typeof child !== 'string' && child.type === DOMVNODE);
}
export function isElementNode(value) {
    return !!value.tagName;
}
function toTextVNode(data) {
    return {
        tag: '',
        properties: {},
        children: undefined,
        text: `${data}`,
        type: VNODE
    };
}
export function w(widgetConstructorOrNode, properties, children) {
    if (properties.__children__) {
        delete properties.__children__;
    }
    if (isWNodeFactory(widgetConstructorOrNode)) {
        return widgetConstructorOrNode(properties, children);
    }
    if (isWNode(widgetConstructorOrNode)) {
        properties = Object.assign({}, widgetConstructorOrNode.properties, properties);
        children = children ? children : widgetConstructorOrNode.children;
        widgetConstructorOrNode = widgetConstructorOrNode.widgetConstructor;
    }
    return {
        children: children || [],
        widgetConstructor: widgetConstructorOrNode,
        properties,
        type: WNODE
    };
}
export function v(tag, propertiesOrChildren = {}, children = undefined) {
    let properties = propertiesOrChildren;
    let deferredPropertiesCallback;
    if (typeof tag.tag === 'function') {
        return tag.tag(properties, children);
    }
    if (Array.isArray(propertiesOrChildren)) {
        children = propertiesOrChildren;
        properties = {};
    }
    if (typeof properties === 'function') {
        deferredPropertiesCallback = properties;
        properties = {};
    }
    if (isVNode(tag)) {
        let { classes = [], styles = {} } = properties, newProperties = __rest(properties, ["classes", "styles"]);
        let _a = tag.properties, { classes: nodeClasses = [], styles: nodeStyles = {} } = _a, nodeProperties = __rest(_a, ["classes", "styles"]);
        nodeClasses = Array.isArray(nodeClasses) ? nodeClasses : [nodeClasses];
        classes = Array.isArray(classes) ? classes : [classes];
        styles = Object.assign({}, nodeStyles, styles);
        properties = Object.assign({}, nodeProperties, newProperties, { classes: [...nodeClasses, ...classes], styles });
        children = children ? children : tag.children;
        tag = tag.tag;
    }
    return {
        tag,
        deferredPropertiesCallback,
        children,
        properties,
        type: VNODE
    };
}
/**
 * Create a VNode for an existing DOM Node.
 */
export function dom({ node, attrs = {}, props = {}, on = {}, diffType = 'none', onAttach, onDetach, onUpdate }, children) {
    return {
        tag: isElementNode(node) ? node.tagName.toLowerCase() : '',
        properties: props,
        attributes: attrs,
        events: on,
        children,
        type: DOMVNODE,
        domNode: node,
        text: isElementNode(node) ? undefined : node.data,
        diffType,
        onAttach,
        onUpdate,
        onDetach
    };
}
export const REGISTRY_ITEM = '__registry_item';
export class FromRegistry {
    constructor() {
        /* tslint:disable-next-line:variable-name */
        this.__properties__ = {};
    }
}
FromRegistry.type = REGISTRY_ITEM;
export function fromRegistry(tag) {
    var _a;
    return _a = class extends FromRegistry {
            constructor() {
                super(...arguments);
                this.properties = {};
                this.name = tag;
            }
        },
        _a.type = REGISTRY_ITEM,
        _a;
}
export function tsx(tag, properties = {}, ...children) {
    children = flat(children, Infinity);
    properties = properties === null ? {} : properties;
    if (typeof tag === 'string') {
        return v(tag, properties, children);
    }
    else if (tag.type === 'registry' && properties.__autoRegistryItem) {
        const name = properties.__autoRegistryItem;
        delete properties.__autoRegistryItem;
        return w(name, properties, children);
    }
    else if (tag.type === REGISTRY_ITEM) {
        const registryItem = new tag();
        return w(registryItem.name, properties, children);
    }
    else {
        return w(tag, properties, children);
    }
}
export function propertiesDiff(current, next, invalidator, ignoreProperties) {
    const propertyNames = [...Object.keys(current), ...Object.keys(next)];
    for (let i = 0; i < propertyNames.length; i++) {
        if (ignoreProperties.indexOf(propertyNames[i]) > -1) {
            continue;
        }
        const result = auto(current[propertyNames[i]], next[propertyNames[i]]);
        if (result.changed) {
            invalidator();
            break;
        }
        ignoreProperties.push(propertyNames[i]);
    }
}
function createClassPropValue(classes = []) {
    let classNames = '';
    if (Array.isArray(classes)) {
        for (let i = 0; i < classes.length; i++) {
            let className = classes[i];
            if (className && className !== true) {
                classNames = classNames ? `${classNames} ${className}` : className;
            }
        }
        return classNames;
    }
    if (classes && classes !== true) {
        classNames = classes;
    }
    return classNames;
}
function arrayFrom(arr) {
    return Array.prototype.slice.call(arr);
}
function createFactory(callback, middlewares, key) {
    const factory = (properties, children) => {
        if (properties) {
            const result = w(callback, properties, children);
            callback.isWidget = true;
            callback.middlewares = middlewares;
            return result;
        }
        return {
            middlewares,
            callback
        };
    };
    const keys = Object.keys(middlewares).reduce((keys, middlewareName) => {
        const middleware = middlewares[middlewareName];
        if (middleware.keys) {
            keys = [...keys, ...middleware.keys];
        }
        return keys;
    }, key ? [key] : []);
    factory.withType = () => {
        return factory;
    };
    callback.keys = keys;
    factory.keys = keys;
    factory.isFactory = true;
    return factory;
}
export function create(middlewares = {}) {
    function properties() {
        function returns(callback) {
            return createFactory(callback, middlewares);
        }
        function key(key) {
            function returns(callback) {
                return createFactory(callback, middlewares, key);
            }
            return returns;
        }
        function children() {
            function returns(callback) {
                return createFactory(callback, middlewares);
            }
            function key(key) {
                function returns(callback) {
                    return createFactory(callback, middlewares, key);
                }
                return returns;
            }
            returns.key = key;
            return returns;
        }
        returns.children = children;
        returns.key = key;
        return returns;
    }
    function children() {
        function properties() {
            function returns(callback) {
                return createFactory(callback, middlewares);
            }
            function key(key) {
                function returns(callback) {
                    return createFactory(callback, middlewares, key);
                }
                return returns;
            }
            returns.key = key;
            return returns;
        }
        function returns(callback) {
            return createFactory(callback, middlewares);
        }
        returns.properties = properties;
        return returns;
    }
    function returns(callback) {
        return createFactory(callback, middlewares);
    }
    returns.children = children;
    returns.properties = properties;
    return returns;
}
const factory = create();
function wrapNodes(renderer) {
    const result = renderer();
    const isWNodeWrapper = isWNode(result);
    const callback = () => {
        return result;
    };
    callback.isWNodeWrapper = isWNodeWrapper;
    return factory(callback);
}
export const widgetInstanceMap = new WeakMap();
const widgetMetaMap = new Map();
const requestedDomNodes = new Set();
let wrapperId = 0;
let metaId = 0;
function addNodeToMap(id, key, node) {
    const widgetMeta = widgetMetaMap.get(id);
    if (widgetMeta) {
        widgetMeta.nodeMap = widgetMeta.nodeMap || new Map();
        widgetMeta.nodeMap.set(key, node);
        if (requestedDomNodes.has(`${id}-${key}`)) {
            widgetMeta.invalidator();
            requestedDomNodes.delete(`${id}-${key}`);
        }
    }
}
function destroyHandles(meta) {
    const { destroyMap, middlewareIds } = meta;
    if (!destroyMap) {
        return;
    }
    for (let i = 0; i < middlewareIds.length; i++) {
        const id = middlewareIds[i];
        const destroy = destroyMap.get(id);
        destroy && destroy();
        destroyMap.delete(id);
        if (destroyMap.size === 0) {
            break;
        }
    }
    destroyMap.clear();
}
function runDiffs(meta, current, next) {
    let customProperties = {};
    meta.customDiffMap = meta.customDiffMap || new Map();
    if (meta.customDiffMap.size) {
        meta.customDiffMap.forEach((diffMap) => {
            diffMap.forEach((diff, propertyName) => {
                const result = diff(Object.assign({}, current), Object.assign({}, next));
                if (result) {
                    customProperties[propertyName] = result;
                }
            });
        });
    }
    return customProperties;
}
export const invalidator = factory(({ id }) => {
    const [widgetId] = id.split('-');
    return () => {
        const widgetMeta = widgetMetaMap.get(widgetId);
        if (widgetMeta) {
            return widgetMeta.invalidator();
        }
    };
});
export const node = factory(({ id }) => {
    return {
        get(key) {
            const [widgetId] = id.split('-');
            const widgetMeta = widgetMetaMap.get(widgetId);
            if (widgetMeta) {
                const { nodeApi } = widgetMeta;
                widgetMeta.nodeMap = widgetMeta.nodeMap || new Map();
                const mountNode = widgetMeta.mountNode;
                const node = widgetMeta.nodeMap.get(key);
                if (node &&
                    (nodeApi.contains(mountNode, node) ||
                        (!nodeApi.equals(nodeApi.getBody(), mountNode) &&
                            nodeApi.contains(nodeApi.getBody(), node)))) {
                    return node;
                }
                requestedDomNodes.add(`${widgetId}-${key}`);
            }
            return null;
        }
    };
});
export const diffProperty = factory(({ id }) => {
    function callback(propertyName, propertiesOrDiff, diff) {
        const [widgetId] = id.split('-');
        const widgetMeta = widgetMetaMap.get(widgetId);
        if (!diff) {
            diff = propertiesOrDiff;
        }
        if (widgetMeta) {
            widgetMeta.customDiffMap = widgetMeta.customDiffMap || new Map();
            widgetMeta.customDiffProperties = widgetMeta.customDiffProperties || new Set();
            const propertyDiffMap = widgetMeta.customDiffMap.get(id) || new Map();
            if (!propertyDiffMap.has(propertyName)) {
                const result = diff({}, widgetMeta.originalProperties);
                if (result !== undefined) {
                    if (has('dojo-debug')) {
                        if (widgetMeta.propertiesCalled) {
                            console.warn(`Calling "propertyDiff" middleware after accessing properties in "${widgetMeta.widgetName}", can result in referencing stale properties.`);
                        }
                    }
                    widgetMeta.properties = Object.assign({}, widgetMeta.properties, { [propertyName]: result });
                }
                propertyDiffMap.set(propertyName, diff);
                widgetMeta.customDiffProperties.add(propertyName);
            }
            widgetMeta.customDiffMap.set(id, propertyDiffMap);
        }
    }
    return callback;
});
export const destroy = factory(({ id }) => {
    return (destroyFunction) => {
        const [widgetId] = id.split('-');
        const widgetMeta = widgetMetaMap.get(widgetId);
        if (widgetMeta) {
            widgetMeta.destroyMap = widgetMeta.destroyMap || new Map();
            if (!widgetMeta.destroyMap.has(id)) {
                widgetMeta.destroyMap.set(id, destroyFunction);
            }
        }
    };
});
export const getRegistry = factory(({ id }) => {
    const [widgetId] = id.split('-');
    return () => {
        const widgetMeta = widgetMetaMap.get(widgetId);
        if (widgetMeta) {
            if (!widgetMeta.registryHandler) {
                widgetMeta.registryHandler = new RegistryHandler();
                widgetMeta.registryHandler.base = widgetMeta.registry;
                widgetMeta.registryHandler.on('invalidate', widgetMeta.invalidator);
            }
            widgetMeta.registryHandler = widgetMeta.registryHandler || new RegistryHandler();
            return widgetMeta.registryHandler;
        }
        return null;
    };
});
export const defer = factory(({ id }) => {
    const [widgetId] = id.split('-');
    let isDeferred = false;
    return {
        pause() {
            const widgetMeta = widgetMetaMap.get(widgetId);
            if (!isDeferred && widgetMeta) {
                widgetMeta.deferRefs = widgetMeta.deferRefs + 1;
                isDeferred = true;
            }
        },
        resume() {
            const widgetMeta = widgetMetaMap.get(widgetId);
            if (isDeferred && widgetMeta) {
                widgetMeta.deferRefs = widgetMeta.deferRefs - 1;
                isDeferred = false;
            }
        }
    };
});
function wrapFunctionProperties(widgetMeta, id, properties) {
    const props = {};
    const propertyNames = Object.keys(properties);
    for (let i = 0; i < propertyNames.length; i++) {
        const propertyName = propertyNames[i];
        if (typeof properties[propertyName] === 'function') {
            let cachedWrapper = widgetMeta.wrappedFunctionMap.get(propertyName);
            if (!cachedWrapper) {
                cachedWrapper = function WrappedProperty(...args) {
                    const widgetMeta = widgetMetaMap.get(id);
                    if (widgetMeta) {
                        return widgetMeta.originalProperties[propertyName](...args);
                    }
                    return properties[propertyName](...args);
                };
                cachedWrapper.unwrap = () => {
                    const widgetMeta = widgetMetaMap.get(id);
                    if (widgetMeta) {
                        return widgetMeta.originalProperties[propertyName];
                    }
                    return properties[propertyName];
                };
                widgetMeta.wrappedFunctionMap.set(propertyName, cachedWrapper);
            }
            props[propertyName] = cachedWrapper;
        }
        else {
            props[propertyName] = properties[propertyName];
        }
    }
    return props;
}
export function renderer(renderer) {
    let _mountOptions = {
        sync: false,
        merge: true,
        transition: undefined,
        registry: new Registry()
    };
    let _invalidationQueue = [];
    let _processQueue = [];
    let _deferredProcessQueue = [];
    let _applicationQueue = [];
    let _eventMap = new WeakMap();
    let _idToWrapperMap = new Map();
    let _wrapperSiblingMap = new WeakMap();
    let _idToChildrenWrappers = new Map();
    let _insertBeforeMap = new WeakMap();
    let _nodeToWrapperMap = new WeakMap();
    let _renderScheduled;
    let _deferredRenderCallbacks = [];
    let parentInvalidate;
    let _allMergedNodes = [];
    let _appWrapperId;
    let _deferredProcessIds = new Map();
    function nodeOperation(propName, propValue, previousValue, domNode) {
        let result = propValue && !previousValue;
        if (typeof propValue === 'function') {
            result = propValue();
        }
        if (result === true) {
            _deferredRenderCallbacks.push(() => {
                _mountOptions.nodeApi.callProperty(domNode, propName);
            });
        }
    }
    function same(dnode1, dnode2) {
        if (isVNodeWrapper(dnode1) && isVNodeWrapper(dnode2)) {
            if (isDomVNode(dnode1.node) && isDomVNode(dnode2.node)) {
                if (!_mountOptions.nodeApi.equals(dnode1.node.domNode, dnode2.node.domNode)) {
                    return false;
                }
            }
            if (dnode1.node.tag !== dnode2.node.tag) {
                return false;
            }
            if (dnode1.node.properties.key !== dnode2.node.properties.key) {
                return false;
            }
            return true;
        }
        else if (isWNodeWrapper(dnode1) && isWNodeWrapper(dnode2)) {
            const widgetConstructor1 = dnode1.registryItem || dnode1.node.widgetConstructor;
            const widgetConstructor2 = dnode2.registryItem || dnode2.node.widgetConstructor;
            const { node: { properties: props1 } } = dnode1;
            const { node: { properties: props2 } } = dnode2;
            if (dnode1.instance === undefined && typeof widgetConstructor2 === 'string') {
                return false;
            }
            if (widgetConstructor1 !== widgetConstructor2) {
                return false;
            }
            if (props1.key !== props2.key) {
                return false;
            }
            if (!(widgetConstructor1.keys || []).every((key) => props1[key] === props2[key])) {
                return false;
            }
            return true;
        }
        return false;
    }
    function findIndexOfChild(children, sameAs, start) {
        for (let i = start; i < children.length; i++) {
            if (same(children[i], sameAs)) {
                return i;
            }
        }
        return -1;
    }
    function checkDistinguishable(wrappers, index, parentWNodeWrapper) {
        const wrapperToCheck = wrappers[index];
        if (isVNodeWrapper(wrapperToCheck) && !wrapperToCheck.node.tag) {
            return;
        }
        const { key } = wrapperToCheck.node.properties;
        let parentName = 'unknown';
        if (parentWNodeWrapper) {
            const { node: { widgetConstructor } } = parentWNodeWrapper;
            parentName = widgetConstructor.name || 'unknown';
        }
        if (key === undefined || key === null) {
            for (let i = 0; i < wrappers.length; i++) {
                if (i !== index) {
                    const wrapper = wrappers[i];
                    if (same(wrapper, wrapperToCheck)) {
                        let nodeIdentifier;
                        if (isWNodeWrapper(wrapper)) {
                            nodeIdentifier = wrapper.node.widgetConstructor.name || 'unknown';
                        }
                        else {
                            nodeIdentifier = wrapper.node.tag;
                        }
                        console.warn(`A widget (${parentName}) has had a child added or removed, but they were not able to uniquely identified. It is recommended to provide a unique 'key' property when using the same widget or element (${nodeIdentifier}) multiple times as siblings`);
                        break;
                    }
                }
            }
        }
    }
    function updateAttribute(domNode, attrName, attrValue, namespace) {
        if (namespace === NAMESPACE_SVG && attrName === 'href' && attrValue) {
            _mountOptions.nodeApi.setAttributeWithNamespace(domNode, NAMESPACE_XLINK, attrName, attrValue);
        }
        else if ((attrName === 'role' && attrValue === '') || attrValue === undefined) {
            _mountOptions.nodeApi.removeAttribute(domNode, attrName);
        }
        else {
            _mountOptions.nodeApi.setAttribute(domNode, attrName, attrValue);
        }
    }
    function updateAttributes(domNode, previousAttributes, attributes, namespace) {
        const attrNames = Object.keys(attributes);
        const attrCount = attrNames.length;
        for (let i = 0; i < attrCount; i++) {
            const attrName = attrNames[i];
            const attrValue = attributes[attrName];
            const previousAttrValue = previousAttributes[attrName];
            if (attrValue !== previousAttrValue) {
                updateAttribute(domNode, attrName, attrValue, namespace);
            }
        }
    }
    function buildPreviousProperties(domNode, current) {
        const { node: { diffType, properties, attributes } } = current;
        if (!diffType || diffType === 'vdom') {
            return {
                properties: current.deferredProperties
                    ? Object.assign({}, current.deferredProperties, current.node.properties) : current.node.properties,
                attributes: current.node.attributes,
                events: current.node.events
            };
        }
        else if (diffType === 'none') {
            return {
                properties: {},
                attributes: current.node.attributes ? {} : undefined,
                events: current.node.events
            };
        }
        let newProperties = {
            properties: {}
        };
        if (attributes) {
            newProperties.attributes = {};
            newProperties.events = current.node.events;
            Object.keys(properties).forEach((propName) => {
                newProperties.properties[propName] = _mountOptions.nodeApi.getProperty(domNode, propName);
            });
            Object.keys(attributes).forEach((attrName) => {
                newProperties.attributes[attrName] = _mountOptions.nodeApi.getAttribute(domNode, attrName);
            });
            return newProperties;
        }
        newProperties.properties = Object.keys(properties).reduce((props, property) => {
            props[property] =
                _mountOptions.nodeApi.getAttribute(domNode, property) ||
                    _mountOptions.nodeApi.getProperty(domNode, property);
            return props;
        }, {});
        return newProperties;
    }
    function updateEvent(domNode, eventName, currentValue, eventOptions) {
        const proxyEvents = _eventMap.get(domNode) || {};
        let proxyEvent = proxyEvents[eventName];
        let callback = currentValue;
        if (eventName === 'input') {
            callback = function (evt) {
                currentValue.call(this, evt);
                evt.target['oninput-value'] = evt.target.value;
            };
        }
        const { passive: currentPassive = [] } = eventOptions || {};
        const isPassive = currentPassive.indexOf(`on${eventName}`) !== -1;
        const options = { passive: isPassive };
        if (proxyEvent && proxyEvent.options.passive !== isPassive) {
            _mountOptions.nodeApi.removeEvent(domNode, eventName, proxyEvent.proxy);
            proxyEvent = undefined;
        }
        if (proxyEvent) {
            proxyEvents[eventName] = Object.assign({}, proxyEvent, { callback });
            _eventMap.set(domNode, proxyEvents);
        }
        else {
            const proxy = (...args) => {
                const proxyEvents = _eventMap.get(domNode) || {};
                const proxyEvent = proxyEvents[eventName];
                proxyEvent && proxyEvent.callback(...args);
            };
            proxyEvents[eventName] = { callback, proxy, options };
            _mountOptions.nodeApi.addEvent(domNode, eventName, proxy, options);
            _eventMap.set(domNode, proxyEvents);
        }
    }
    function removeOrphanedEvents(domNode, previousProperties, properties, onlyEvents = false) {
        Object.keys(previousProperties).forEach((propName) => {
            const isEvent = propName.substr(0, 2) === 'on' || onlyEvents;
            const eventName = onlyEvents ? propName : propName.substr(2);
            if (isEvent && !properties[propName]) {
                const proxyEvents = _eventMap.get(domNode) || {};
                let proxyEvent = proxyEvents[eventName];
                if (proxyEvent) {
                    _mountOptions.nodeApi.removeEvent(domNode, eventName, proxyEvent.proxy);
                    delete proxyEvents[eventName];
                    _eventMap.set(domNode, proxyEvents);
                }
            }
        });
    }
    function resolveRegistryItem(wrapper, instance, id) {
        if (!isWidget(wrapper.node.widgetConstructor)) {
            const owningNode = _nodeToWrapperMap.get(wrapper.node);
            if (owningNode) {
                if (owningNode.instance) {
                    instance = owningNode.instance;
                }
                else {
                    id = owningNode.id;
                }
            }
            let registry;
            if (instance) {
                const instanceData = widgetInstanceMap.get(instance);
                if (instanceData) {
                    registry = instanceData.registry;
                }
            }
            else if (id !== undefined) {
                const widgetMeta = widgetMetaMap.get(id);
                if (widgetMeta) {
                    if (!widgetMeta.registryHandler) {
                        widgetMeta.registryHandler = new RegistryHandler();
                        widgetMeta.registryHandler.base = widgetMeta.registry;
                        widgetMeta.registryHandler.on('invalidate', widgetMeta.invalidator);
                    }
                    registry = widgetMeta.registryHandler;
                }
            }
            if (registry) {
                let registryLabel;
                if (isLazyDefine(wrapper.node.widgetConstructor)) {
                    const { label, registryItem } = wrapper.node.widgetConstructor;
                    if (!registry.has(label)) {
                        registry.define(label, registryItem);
                    }
                    registryLabel = label;
                }
                else {
                    registryLabel = wrapper.node.widgetConstructor;
                }
                let item = registry.get(registryLabel);
                if (isWNodeFactory(item)) {
                    const node = item(wrapper.node.properties, wrapper.node.children);
                    if (isWidgetFunction(node.widgetConstructor)) {
                        wrapper.registryItem = node.widgetConstructor;
                    }
                }
                else {
                    wrapper.registryItem = item;
                }
            }
        }
    }
    function mapNodeToInstance(nodes, wrapper) {
        while (nodes.length) {
            let node = nodes.pop();
            if (isWNode(node) || isVNode(node)) {
                if (!_nodeToWrapperMap.has(node)) {
                    _nodeToWrapperMap.set(node, wrapper);
                    if (node.children && node.children.length) {
                        nodes = [...nodes, ...node.children];
                    }
                }
            }
        }
    }
    function renderedToWrapper(rendered, parent, currentParent) {
        const { requiresInsertBefore, hasPreviousSiblings, namespace, depth } = parent;
        const wrappedRendered = [];
        const hasParentWNode = isWNodeWrapper(parent);
        const hasVirtualParentNode = isVirtualWrapper(parent);
        const currentParentChildren = (isVNodeWrapper(currentParent) && _idToChildrenWrappers.get(currentParent.id)) || [];
        const hasCurrentParentChildren = currentParentChildren.length > 0;
        const insertBefore = ((requiresInsertBefore || hasPreviousSiblings !== false) && (hasParentWNode || hasVirtualParentNode)) ||
            (hasCurrentParentChildren && rendered.length > 1);
        let previousItem;
        if (isWNodeWrapper(parent) && rendered.length) {
            mapNodeToInstance([...rendered], parent);
        }
        for (let i = 0; i < rendered.length; i++) {
            let renderedItem = rendered[i];
            if (!renderedItem || renderedItem === true) {
                continue;
            }
            if (typeof renderedItem === 'string' || typeof renderedItem === 'number') {
                renderedItem = toTextVNode(renderedItem);
            }
            const owningNode = _nodeToWrapperMap.get(renderedItem);
            const wrapper = {
                node: renderedItem,
                depth: depth + 1,
                order: i,
                parentId: parent.id,
                requiresInsertBefore: insertBefore,
                hasParentWNode,
                namespace: namespace,
                reparent: hasParentWNode ? parent.reparent : undefined
            };
            if (isVNode(renderedItem)) {
                if (renderedItem.deferredPropertiesCallback) {
                    wrapper.deferredProperties = renderedItem.deferredPropertiesCallback(false);
                }
                if (renderedItem.properties.exitAnimation) {
                    parent.hasAnimations = true;
                    let nextParent = _idToWrapperMap.get(parent.parentId);
                    while (nextParent) {
                        if (nextParent.hasAnimations) {
                            break;
                        }
                        nextParent.hasAnimations = true;
                        nextParent = _idToWrapperMap.get(nextParent.parentId);
                    }
                }
            }
            if (owningNode) {
                wrapper.owningId = owningNode.id;
            }
            if (isWNode(renderedItem)) {
                resolveRegistryItem(wrapper, parent.instance, parent.id);
            }
            if (previousItem) {
                _wrapperSiblingMap.set(previousItem, wrapper);
            }
            wrappedRendered.push(wrapper);
            previousItem = wrapper;
        }
        return wrappedRendered;
    }
    function findParentDomNode(currentNode) {
        let parentDomNode;
        let parentWrapper = _idToWrapperMap.get(currentNode.parentId);
        while (!parentDomNode && parentWrapper) {
            if (!parentDomNode &&
                isVNodeWrapper(parentWrapper) &&
                !isVirtualWrapper(parentWrapper) &&
                parentWrapper.domNode) {
                parentDomNode = parentWrapper.domNode;
            }
            parentWrapper = _idToWrapperMap.get(parentWrapper.parentId);
        }
        return parentDomNode;
    }
    function runDeferredProperties(next) {
        const { deferredPropertiesCallback } = next.node;
        if (deferredPropertiesCallback) {
            const properties = next.node.properties;
            _deferredRenderCallbacks.push(() => {
                if (_idToWrapperMap.has(next.owningId)) {
                    const deferredProperties = next.deferredProperties;
                    next.deferredProperties = deferredPropertiesCallback(true);
                    processProperties(next, {
                        properties: Object.assign({}, deferredProperties, properties)
                    });
                }
            });
        }
    }
    function findInsertBefore(next) {
        let insertBefore = null;
        let searchNode = next;
        while (!insertBefore) {
            const nextSibling = _wrapperSiblingMap.get(searchNode);
            if (nextSibling) {
                if (isBodyWrapper(nextSibling) || isHeadWrapper(nextSibling)) {
                    searchNode = nextSibling;
                    continue;
                }
                let domNode = nextSibling.domNode;
                if (isWNodeWrapper(nextSibling) || isVirtualWrapper(nextSibling)) {
                    if (!nextSibling.childDomWrapperId) {
                        nextSibling.childDomWrapperId = findDomNodeOnParentWrapper(nextSibling.id);
                    }
                    if (nextSibling.childDomWrapperId) {
                        const childWrapper = _idToWrapperMap.get(nextSibling.childDomWrapperId);
                        if (childWrapper &&
                            !childWrapper.reparent &&
                            !isBodyWrapper(childWrapper) &&
                            !isHeadWrapper(childWrapper)) {
                            domNode = childWrapper.domNode;
                        }
                    }
                }
                if (!nextSibling.reparent && domNode && _mountOptions.nodeApi.getParent(domNode)) {
                    insertBefore = domNode;
                    break;
                }
                searchNode = nextSibling;
                continue;
            }
            searchNode = searchNode && _idToWrapperMap.get(searchNode.parentId);
            if (!searchNode || (isVNodeWrapper(searchNode) && !isVirtualWrapper(searchNode))) {
                break;
            }
        }
        return insertBefore;
    }
    function setValue(domNode, propValue, previousValue) {
        const domValue = _mountOptions.nodeApi.getProperty(domNode, 'value');
        const onInputValue = _mountOptions.nodeApi.getProperty(domNode, 'oninput-value');
        const onSelectValue = _mountOptions.nodeApi.getProperty(domNode, 'select-value');
        const valueChanged = domValue !== propValue;
        if (onSelectValue && domValue !== onSelectValue) {
            _mountOptions.nodeApi.setProperty(domNode, 'value', onSelectValue);
            if (domValue === onSelectValue) {
                _mountOptions.nodeApi.setProperty(domNode, 'select-value', undefined);
            }
        }
        else if (valueChanged && ((onInputValue && domValue === onInputValue) || propValue !== previousValue)) {
            _mountOptions.nodeApi.setProperty(domNode, 'value', propValue);
            _mountOptions.nodeApi.setProperty(domNode, 'oninput-value', undefined);
        }
    }
    function setProperties(domNode, currentProperties = {}, nextWrapper, includesEventsAndAttributes = true) {
        const properties = nextWrapper.deferredProperties
            ? Object.assign({}, nextWrapper.deferredProperties, nextWrapper.node.properties) : nextWrapper.node.properties;
        const propNames = Object.keys(properties);
        const propCount = propNames.length;
        if (propNames.indexOf('classes') === -1 && currentProperties.classes) {
            _mountOptions.nodeApi.removeAttribute(domNode, 'class');
        }
        includesEventsAndAttributes && removeOrphanedEvents(domNode, currentProperties, properties);
        for (let i = 0; i < propCount; i++) {
            const propName = propNames[i];
            let propValue = properties[propName];
            const previousValue = currentProperties[propName];
            if (propName === 'classes') {
                const previousClassString = createClassPropValue(previousValue);
                let currentClassString = createClassPropValue(propValue);
                if (previousClassString !== currentClassString) {
                    if (currentClassString) {
                        if (nextWrapper.merged) {
                            const domClasses = (_mountOptions.nodeApi.getAttribute(domNode, 'class') || '').split(' ');
                            for (let i = 0; i < domClasses.length; i++) {
                                if (currentClassString.indexOf(domClasses[i]) === -1) {
                                    currentClassString = `${domClasses[i]} ${currentClassString}`;
                                }
                            }
                        }
                        _mountOptions.nodeApi.setAttribute(domNode, 'class', currentClassString);
                    }
                    else {
                        _mountOptions.nodeApi.removeAttribute(domNode, 'class');
                    }
                }
            }
            else if (nodeOperations.indexOf(propName) !== -1) {
                nodeOperation(propName, propValue, previousValue, domNode);
            }
            else if (propName === 'styles') {
                const styleNames = Object.keys(propValue);
                const styleCount = styleNames.length;
                for (let j = 0; j < styleCount; j++) {
                    const styleName = styleNames[j];
                    const newStyleValue = propValue[styleName];
                    const oldStyleValue = previousValue && previousValue[styleName];
                    if (newStyleValue === oldStyleValue) {
                        continue;
                    }
                    _mountOptions.nodeApi.setStyle(domNode, styleName, newStyleValue || '');
                }
            }
            else {
                if (!propValue && typeof previousValue === 'string') {
                    propValue = '';
                }
                if (propName === 'value') {
                    const tagName = _mountOptions.nodeApi.getTag(domNode);
                    if (tagName === 'SELECT') {
                        _mountOptions.nodeApi.setProperty(domNode, 'select-value', propValue);
                    }
                    setValue(domNode, propValue, previousValue);
                }
                else if (propName !== 'key') {
                    const type = typeof propValue;
                    if (type === 'function' &&
                        propName.lastIndexOf('on', 0) === 0 &&
                        includesEventsAndAttributes &&
                        (propValue !== previousValue || properties.oneventoptions)) {
                        updateEvent(domNode, propName.substr(2), propValue, properties.oneventoptions);
                    }
                    else if (propName === 'oneventoptions') {
                    }
                    else if (propValue !== previousValue) {
                        if (type === 'string' && propName !== 'innerHTML' && includesEventsAndAttributes) {
                            updateAttribute(domNode, propName, propValue, nextWrapper.namespace);
                        }
                        else if (propName === 'scrollLeft' || propName === 'scrollTop') {
                            const currentValue = _mountOptions.nodeApi.getProperty(domNode, propName);
                            if (currentValue !== propValue) {
                                _mountOptions.nodeApi.setProperty(domNode, propName, propValue);
                            }
                        }
                        else {
                            _mountOptions.nodeApi.setProperty(domNode, propName, propValue);
                        }
                    }
                }
            }
        }
    }
    function _createDeferredRenderCallback() {
        const callbacks = _deferredRenderCallbacks;
        _deferredRenderCallbacks = [];
        if (callbacks.length) {
            return () => {
                let callback;
                while ((callback = callbacks.shift())) {
                    callback();
                }
            };
        }
    }
    function _scheduleDeferredRenderCallbacks() {
        const { sync } = _mountOptions;
        const run = _createDeferredRenderCallback();
        if (run) {
            if (sync) {
                run();
            }
            else {
                let id;
                id = _mountOptions.nodeApi.requestAnimationFrame(() => {
                    _deferredProcessIds.delete(id);
                    run();
                });
                _deferredProcessIds.set(id, run);
            }
        }
    }
    function processProperties(next, previousProperties) {
        if (next.node.attributes && next.node.events) {
            updateAttributes(next.domNode, previousProperties.attributes || {}, next.node.attributes, next.namespace);
            setProperties(next.domNode, previousProperties.properties, next, false);
            const events = next.node.events || {};
            if (previousProperties.events) {
                removeOrphanedEvents(next.domNode, previousProperties.events || {}, next.node.events, true);
            }
            previousProperties.events = previousProperties.events || {};
            Object.keys(events).forEach((event) => {
                updateEvent(next.domNode, event, events[event]);
            });
        }
        else {
            setProperties(next.domNode, previousProperties.properties, next);
        }
    }
    function unmount() {
        _processQueue.push({
            current: [_idToWrapperMap.get(_appWrapperId)],
            next: [],
            meta: {}
        });
        if (_renderScheduled) {
            _mountOptions.nodeApi.cancelAnimationFrame(_renderScheduled);
        }
        _runProcessQueue();
        _runDomInstructionQueue();
        _deferredProcessIds.forEach((callback, id) => {
            _mountOptions.nodeApi.cancelAnimationFrame(id);
            callback();
        });
        const run = _createDeferredRenderCallback();
        run && run();
        _invalidationQueue = [];
        _processQueue = [];
        _deferredProcessQueue = [];
        _applicationQueue = [];
        _deferredRenderCallbacks = [];
        _allMergedNodes = [];
        _eventMap = new WeakMap();
        _idToWrapperMap.clear();
        _idToChildrenWrappers.clear();
        _wrapperSiblingMap = new WeakMap();
        _nodeToWrapperMap = new WeakMap();
        _insertBeforeMap = undefined;
    }
    function mount(mountOptions = {}) {
        let domNode = mountOptions.domNode;
        const nodeApi = mountOptions.nodeApi || defaultNodeApi;
        if (!domNode) {
            if (has('dojo-debug') && domNode === null) {
                console.warn('Unable to find node to mount the application, defaulting to the document body.');
            }
            domNode = nodeApi.getBody();
        }
        _mountOptions = Object.assign({}, _mountOptions, mountOptions, { domNode, nodeApi });
        const renderResult = wrapNodes(renderer)({}, []);
        _appWrapperId = `${wrapperId++}`;
        const nextWrapper = {
            id: _appWrapperId,
            node: renderResult,
            order: 0,
            depth: 1,
            owningId: '-1',
            parentId: '-1',
            siblingId: '-1',
            properties: {}
        };
        _idToWrapperMap.set('-1', {
            id: `-1`,
            depth: 0,
            order: 0,
            owningId: '',
            domNode,
            node: v('fake'),
            parentId: '-1'
        });
        _processQueue.push({
            current: [],
            next: [nextWrapper],
            meta: { mergeNodes: arrayFrom(_mountOptions.nodeApi.getChildren(domNode)) }
        });
        _runProcessQueue();
        _runDomInstructionQueue();
        _cleanUpMergedNodes();
        _insertBeforeMap = undefined;
        _scheduleDeferredRenderCallbacks();
        if (!_renderScheduled) {
            setRendering(false);
        }
    }
    function invalidate() {
        parentInvalidate && parentInvalidate();
    }
    function _schedule() {
        const { sync } = _mountOptions;
        if (sync) {
            _runInvalidationQueue();
        }
        else if (!_renderScheduled) {
            setRendering(true);
            _renderScheduled = _mountOptions.nodeApi.requestAnimationFrame(() => {
                _runInvalidationQueue();
            });
        }
    }
    function getWNodeWrapper(id) {
        const wrapper = _idToWrapperMap.get(id);
        if (wrapper && isWNodeWrapper(wrapper)) {
            return wrapper;
        }
    }
    function _runInvalidationQueue() {
        _renderScheduled = undefined;
        let invalidationQueue = [..._invalidationQueue];
        const previouslyRendered = [];
        _invalidationQueue = [];
        invalidationQueue.sort((a, b) => {
            let result = b.depth - a.depth;
            if (result === 0) {
                result = b.order - a.order;
            }
            return result;
        });
        if (_deferredProcessQueue.length) {
            _processQueue = [..._deferredProcessQueue];
            _deferredProcessQueue = [];
            _runProcessQueue();
            if (_deferredProcessQueue.length) {
                _invalidationQueue = [...invalidationQueue];
                invalidationQueue = [];
            }
        }
        let item;
        while ((item = invalidationQueue.pop())) {
            let { id } = item;
            const current = getWNodeWrapper(id);
            if (!current || previouslyRendered.indexOf(id) !== -1 || !_idToWrapperMap.has(current.parentId)) {
                continue;
            }
            previouslyRendered.push(id);
            const sibling = _wrapperSiblingMap.get(current);
            const next = {
                node: {
                    type: WNODE,
                    widgetConstructor: current.node.widgetConstructor,
                    properties: current.properties || {},
                    children: current.node.children || []
                },
                instance: current.instance,
                id: current.id,
                properties: current.properties,
                depth: current.depth,
                order: current.order,
                owningId: current.owningId,
                parentId: current.parentId,
                registryItem: current.registryItem
            };
            sibling && _wrapperSiblingMap.set(next, sibling);
            const result = _updateWidget({ current, next });
            if (result && result.item) {
                _processQueue.push(result.item);
                _idToWrapperMap.set(id, next);
                _runProcessQueue();
            }
        }
        _runDomInstructionQueue();
        _cleanUpMergedNodes();
        _scheduleDeferredRenderCallbacks();
        if (!_renderScheduled) {
            setRendering(false);
        }
    }
    function _cleanUpMergedNodes() {
        if (_deferredProcessQueue.length === 0) {
            let mergedNode;
            while ((mergedNode = _allMergedNodes.pop())) {
                const parent = _mountOptions.nodeApi.getParent(mergedNode);
                parent && _mountOptions.nodeApi.removeChild(parent, mergedNode);
            }
            _mountOptions.merge = false;
        }
    }
    function _runProcessQueue() {
        let item;
        while ((item = _processQueue.pop())) {
            if (isAttachApplication(item)) {
                item.instance && _applicationQueue.push(item);
            }
            else {
                const { current, next, meta } = item;
                _process(current || EMPTY_ARRAY, next || EMPTY_ARRAY, meta);
            }
        }
    }
    function _runDomInstructionQueue() {
        _applicationQueue.reverse();
        let item;
        while ((item = _applicationQueue.pop())) {
            if (item.type === 'create') {
                const { parentDomNode, next, next: { domNode, merged, requiresInsertBefore, node } } = item;
                processProperties(next, { properties: {} });
                runDeferredProperties(next);
                if (!merged) {
                    let insertBefore;
                    if (requiresInsertBefore) {
                        insertBefore = findInsertBefore(next);
                    }
                    else if (_insertBeforeMap) {
                        insertBefore = _insertBeforeMap.get(next);
                    }
                    _mountOptions.nodeApi.insertBefore(parentDomNode, domNode, insertBefore);
                    if (isDomVNode(next.node) && next.node.onAttach) {
                        next.node.onAttach();
                    }
                }
                const parentNode = _mountOptions.nodeApi.getParent(domNode);
                const tagName = _mountOptions.nodeApi.getTag(domNode);
                if (tagName === 'OPTION' && parentNode) {
                    setValue(parentNode);
                }
                const { enterAnimation, enterAnimationActive } = node.properties;
                if (_mountOptions.transition && enterAnimation && enterAnimation !== true) {
                    _mountOptions.transition.enter(domNode, enterAnimation, enterAnimationActive);
                }
                const owningWrapper = _nodeToWrapperMap.get(next.node);
                if (owningWrapper && node.properties.key != null) {
                    if (owningWrapper.instance) {
                        const instanceData = widgetInstanceMap.get(owningWrapper.instance);
                        instanceData && instanceData.nodeHandler.add(domNode, `${node.properties.key}`);
                    }
                    else {
                        addNodeToMap(owningWrapper.id, node.properties.key, domNode);
                    }
                }
                item.next.inserted = true;
                item.next.reparent = undefined;
            }
            else if (item.type === 'update') {
                const { next, next: { domNode }, current, current: { domNode: currentDomNode } } = item;
                if (_mountOptions.nodeApi.isTextNode(domNode) &&
                    _mountOptions.nodeApi.isTextNode(currentDomNode) &&
                    domNode !== currentDomNode) {
                    const parent = _mountOptions.nodeApi.getParent(currentDomNode);
                    if (parent) {
                        _mountOptions.nodeApi.replaceChild(parent, domNode, currentDomNode);
                    }
                }
                else {
                    const previousProperties = buildPreviousProperties(domNode, current);
                    processProperties(next, previousProperties);
                    runDeferredProperties(next);
                }
                if (isDomVNode(next.node) && next.node.onUpdate) {
                    next.node.onUpdate();
                }
            }
            else if (item.type === 'delete') {
                const { current } = item;
                const { exitAnimation, exitAnimationActive } = current.node.properties;
                if (_mountOptions.transition && exitAnimation && exitAnimation !== true) {
                    _mountOptions.transition.exit(current.domNode, exitAnimation, exitAnimationActive);
                }
                else if (current.domNode) {
                    const parent = _mountOptions.nodeApi.getParent(current.domNode);
                    _mountOptions.nodeApi.removeChild(parent, current.domNode);
                }
                if (isDomVNode(current.node) && current.node.onDetach) {
                    current.node.onDetach();
                }
            }
            else if (item.type === 'attach') {
                const { instance, attached } = item;
                const instanceData = widgetInstanceMap.get(instance);
                if (instanceData) {
                    instanceData.nodeHandler.addRoot();
                    attached && instanceData.onAttach();
                }
            }
            else if (item.type === 'detach') {
                if (item.current.instance) {
                    const instanceData = widgetInstanceMap.get(item.current.instance);
                    instanceData && instanceData.onDetach();
                }
                item.current.instance = undefined;
            }
        }
        if (_deferredProcessQueue.length === 0) {
            _nodeToWrapperMap = new WeakMap();
        }
    }
    function _processMergeNodes(next, mergeNodes) {
        const { merge } = _mountOptions;
        if (merge && mergeNodes.length) {
            if (isVNodeWrapper(next) && !isVirtualWrapper(next)) {
                let { node: { tag } } = next;
                for (let i = 0; i < mergeNodes.length; i++) {
                    const domElement = mergeNodes[i];
                    const tagName = domElement.tagName || '';
                    if (tag.toUpperCase() === tagName.toUpperCase()) {
                        const mergeNodeIndex = _allMergedNodes.indexOf(domElement);
                        if (mergeNodeIndex !== -1) {
                            _allMergedNodes.splice(mergeNodeIndex, 1);
                        }
                        mergeNodes.splice(i, 1);
                        next.domNode = domElement;
                        break;
                    }
                }
            }
            else {
                next.mergeNodes = mergeNodes;
            }
        }
    }
    function distinguishableCheck(childNodes, index) {
        const parentWNodeWrapper = getWNodeWrapper(childNodes[index].owningId);
        checkDistinguishable(childNodes, index, parentWNodeWrapper);
    }
    function createKeyMap(wrappers) {
        const keys = new Map();
        for (let i = 0; i < wrappers.length; i++) {
            const wrapper = wrappers[i];
            if (wrapper.node.properties.key != null) {
                keys.set(wrapper.node.properties.key, wrapper);
            }
        }
        return keys;
    }
    function _process(current, next, meta = {}) {
        let { mergeNodes = [], oldIndex = 0, newIndex = 0, currentKeyMap } = meta;
        const currentLength = current.length;
        const nextLength = next.length;
        const hasPreviousSiblings = currentLength > 1 || (currentLength > 0 && currentLength < nextLength);
        let instructions = [];
        let replace = false;
        if (!currentKeyMap) {
            currentKeyMap = createKeyMap(current);
            if (currentKeyMap.size === currentLength) {
                const nextKeyMap = createKeyMap(next);
                if (nextKeyMap.size === nextLength) {
                    const currentEntries = [...currentKeyMap.entries()];
                    for (let i = 0; i < currentEntries.length; i++) {
                        const [key, value] = currentEntries[i];
                        if (nextKeyMap.has(key)) {
                            instructions = [];
                            replace = false;
                            break;
                        }
                        replace = true;
                        instructions.push({ current: value, next: undefined });
                    }
                }
            }
        }
        if (replace || (currentLength === 0 && !_mountOptions.merge)) {
            for (let i = 0; i < nextLength; i++) {
                instructions.push({ current: undefined, next: next[i] });
            }
        }
        else {
            if (newIndex < nextLength) {
                let currentWrapper = oldIndex < currentLength ? current[oldIndex] : undefined;
                const nextWrapper = next[newIndex];
                nextWrapper.hasPreviousSiblings = hasPreviousSiblings;
                _processMergeNodes(nextWrapper, mergeNodes);
                if (currentWrapper && same(currentWrapper, nextWrapper)) {
                    oldIndex++;
                    newIndex++;
                    if (isVNodeWrapper(currentWrapper) && isVNodeWrapper(nextWrapper)) {
                        nextWrapper.inserted = currentWrapper.inserted;
                    }
                    if (isVNodeWrapper(currentWrapper) && isVNodeWrapper(nextWrapper) && nextWrapper.reparent) {
                        nextWrapper.domNode = currentWrapper.domNode;
                        nextWrapper.reparent = currentWrapper.id;
                        currentWrapper.reparent = currentWrapper.id;
                        instructions.push({ current: currentWrapper, next: undefined });
                        instructions.push({ current: undefined, next: nextWrapper });
                    }
                    else {
                        instructions.push({ current: currentWrapper, next: nextWrapper });
                    }
                }
                else if (!currentWrapper || findIndexOfChild(current, nextWrapper, oldIndex + 1) === -1) {
                    has('dojo-debug') && currentLength && distinguishableCheck(next, newIndex);
                    const foundWrapper = currentKeyMap.get(nextWrapper.node.properties.key);
                    if (foundWrapper && same(foundWrapper, nextWrapper)) {
                        const { domNode, id } = foundWrapper;
                        nextWrapper.domNode = domNode;
                        nextWrapper.reparent = id;
                        currentKeyMap.delete(nextWrapper.node.properties.key);
                    }
                    instructions.push({ current: undefined, next: nextWrapper });
                    newIndex++;
                }
                else if (findIndexOfChild(next, currentWrapper, newIndex + 1) === -1) {
                    has('dojo-debug') && distinguishableCheck(current, oldIndex);
                    instructions.push({ current: currentWrapper, next: undefined });
                    oldIndex++;
                }
                else {
                    has('dojo-debug') && distinguishableCheck(next, newIndex);
                    has('dojo-debug') && distinguishableCheck(current, oldIndex);
                    const foundWrapper = currentKeyMap.get(nextWrapper.node.properties.key);
                    if (foundWrapper && same(foundWrapper, nextWrapper)) {
                        const { domNode, id } = foundWrapper;
                        nextWrapper.domNode = domNode;
                        nextWrapper.reparent = id;
                        foundWrapper.reparent = id;
                        currentWrapper.reparent = id;
                        currentKeyMap.delete(nextWrapper.node.properties.key);
                        instructions.push({ current: foundWrapper, next: undefined });
                    }
                    instructions.push({ current: currentWrapper, next: undefined });
                    instructions.push({ current: undefined, next: nextWrapper });
                    oldIndex++;
                    newIndex++;
                }
            }
            if (newIndex < nextLength) {
                _processQueue.push({
                    current,
                    next,
                    meta: { mergeNodes, oldIndex, newIndex, currentKeyMap }
                });
            }
            if (currentLength > oldIndex && newIndex >= nextLength) {
                currentKeyMap.clear();
                for (let i = oldIndex; i < currentLength; i++) {
                    has('dojo-debug') && distinguishableCheck(current, i);
                    instructions.push({ current: current[i], next: undefined });
                }
            }
        }
        for (let i = 0; i < instructions.length; i++) {
            const result = _processOne(instructions[i]);
            if (result === false) {
                if (_mountOptions.merge && mergeNodes.length) {
                    if (newIndex < nextLength) {
                        _processQueue.pop();
                    }
                    _processQueue.push({ next, current, meta });
                    _deferredProcessQueue = _processQueue;
                    _processQueue = [];
                    break;
                }
                continue;
            }
            const { widget, item, dom } = result;
            widget && _processQueue.push(widget);
            item && _processQueue.push(item);
            dom && _applicationQueue.push(dom);
        }
    }
    function _processOne({ current, next }) {
        if (current !== next) {
            if (!current && next) {
                if (isVNodeWrapper(next)) {
                    return _createDom({ next });
                }
                else {
                    return _createWidget({ next });
                }
            }
            else if (current && next) {
                if (isVNodeWrapper(current) && isVNodeWrapper(next)) {
                    return _updateDom({ current, next });
                }
                else if (isWNodeWrapper(current) && isWNodeWrapper(next)) {
                    return _updateWidget({ current, next });
                }
            }
            else if (current && !next) {
                if (isVNodeWrapper(current)) {
                    return _removeDom({ current });
                }
                else if (isWNodeWrapper(current)) {
                    return _removeWidget({ current });
                }
            }
        }
        return {};
    }
    function createWidgetOptions(id, widgetId, middleware) {
        return {
            id,
            properties: () => {
                const widgetMeta = widgetMetaMap.get(widgetId);
                if (widgetMeta) {
                    widgetMeta.propertiesCalled = true;
                    return Object.assign({}, widgetMeta.properties);
                }
                return {};
            },
            children: () => {
                const widgetMeta = widgetMetaMap.get(widgetId);
                if (widgetMeta) {
                    return widgetMeta.children;
                }
                return [];
            },
            middleware
        };
    }
    function resolveMiddleware(middlewares, id, middlewareIds = []) {
        const keys = Object.keys(middlewares);
        const results = {};
        const uniqueId = `${id}-${metaId++}`;
        for (let i = 0; i < keys.length; i++) {
            const middleware = middlewares[keys[i]]();
            const payload = createWidgetOptions(uniqueId, id);
            if (middleware.middlewares) {
                const { middlewares: resolvedMiddleware } = resolveMiddleware(middleware.middlewares, id, middlewareIds);
                payload.middleware = resolvedMiddleware;
                results[keys[i]] = middleware.callback(payload);
            }
            else {
                results[keys[i]] = middleware.callback(payload);
            }
        }
        middlewareIds.push(uniqueId);
        return { middlewares: results, ids: middlewareIds };
    }
    function _createWidget({ next }) {
        let { node: { widgetConstructor } } = next;
        let { registry } = _mountOptions;
        let Constructor = next.registryItem || widgetConstructor;
        if (!isWidget(Constructor)) {
            resolveRegistryItem(next);
            if (!next.registryItem) {
                return false;
            }
            Constructor = next.registryItem;
        }
        let rendered;
        let invalidate;
        next.properties = Object.assign({}, next.node.properties);
        next.id = next.id || `${wrapperId++}`;
        _idToWrapperMap.set(next.id, next);
        const { id, depth, order } = next;
        if (!isWidgetBaseConstructor(Constructor)) {
            let widgetMeta = widgetMetaMap.get(id);
            if (!widgetMeta) {
                invalidate = () => {
                    const widgetMeta = widgetMetaMap.get(id);
                    if (widgetMeta) {
                        widgetMeta.dirty = true;
                        if (!widgetMeta.rendering && _idToWrapperMap.has(id)) {
                            _invalidationQueue.push({ id, depth, order });
                            _schedule();
                        }
                    }
                };
                widgetMeta = {
                    widgetName: Constructor.name || 'unknown',
                    nodeApi: _mountOptions.nodeApi,
                    mountNode: _mountOptions.domNode,
                    dirty: false,
                    invalidator: invalidate,
                    originalProperties: Object.assign({}, next.node.properties),
                    children: next.node.children,
                    deferRefs: 0,
                    rendering: true,
                    middleware: {},
                    middlewareIds: [],
                    registry: _mountOptions.registry,
                    propertiesCalled: false,
                    wrappedFunctionMap: new Map(),
                    properties: {}
                };
                (widgetMeta.properties = wrapFunctionProperties(widgetMeta, id, next.node.properties)),
                    widgetMetaMap.set(next.id, widgetMeta);
                if (Constructor.middlewares && Object.keys(Constructor.middlewares).length) {
                    const { middlewares, ids } = resolveMiddleware(Constructor.middlewares, id);
                    widgetMeta.middleware = middlewares;
                    widgetMeta.middlewareIds = ids;
                }
            }
            else {
                invalidate = widgetMeta.invalidator;
            }
            rendered = Constructor(createWidgetOptions(id, id, widgetMeta.middleware));
            widgetMeta.rendering = false;
            widgetMeta.propertiesCalled = false;
            if (widgetMeta.deferRefs > 0) {
                return false;
            }
        }
        else {
            let instance = new Constructor();
            instance.registry.base = registry;
            const instanceData = widgetInstanceMap.get(instance);
            invalidate = () => {
                instanceData.dirty = true;
                if (!instanceData.rendering && _idToWrapperMap.has(id)) {
                    _invalidationQueue.push({ id, depth, order });
                    _schedule();
                }
            };
            instanceData.invalidate = invalidate;
            instanceData.rendering = true;
            instance.__setProperties__(next.node.properties);
            instance.__setChildren__(next.node.children);
            next.instance = instance;
            rendered = instance.__render__();
            instanceData.rendering = false;
        }
        let children;
        if (rendered) {
            rendered = Array.isArray(rendered) ? rendered : [rendered];
            children = renderedToWrapper(rendered, next, null);
            _idToChildrenWrappers.set(id, children);
        }
        if (!parentInvalidate && !Constructor.isWNodeWrapper) {
            parentInvalidate = invalidate;
        }
        let currentChildren = undefined;
        if (next.reparent) {
            currentChildren = _idToChildrenWrappers.get(next.reparent);
            _idToChildrenWrappers.delete(next.reparent);
            next.reparent = undefined;
        }
        return {
            item: {
                current: currentChildren,
                next: children,
                meta: { mergeNodes: next.mergeNodes }
            },
            widget: { type: 'attach', instance: next.instance, id, attached: true }
        };
    }
    function _updateWidget({ current, next }) {
        current = getWNodeWrapper(current.id) || current;
        const { instance, domNode, hasAnimations, id } = current;
        let { node: { widgetConstructor } } = next;
        const Constructor = next.registryItem || widgetConstructor;
        if (!isWidget(Constructor)) {
            return {};
        }
        let rendered;
        let processResult = {};
        let didRender = false;
        let currentChildren = _idToChildrenWrappers.get(current.id);
        next.hasAnimations = hasAnimations;
        next.id = id;
        next.properties = Object.assign({}, next.node.properties);
        _wrapperSiblingMap.delete(current);
        if (domNode && _mountOptions.nodeApi.getParent(domNode)) {
            next.domNode = domNode;
        }
        if (!isWidgetBaseConstructor(Constructor)) {
            const widgetMeta = widgetMetaMap.get(id);
            if (widgetMeta) {
                widgetMeta.originalProperties = Object.assign({}, next.properties);
                widgetMeta.properties = wrapFunctionProperties(widgetMeta, id, widgetMeta.originalProperties);
                widgetMeta.children = next.node.children;
                widgetMeta.rendering = true;
                const customProperties = runDiffs(widgetMeta, current.properties, widgetMeta.originalProperties);
                widgetMeta.properties = Object.assign({}, widgetMeta.properties, customProperties);
                if (current.node.children.length > 0 || next.node.children.length > 0) {
                    widgetMeta.dirty = true;
                }
                if (!widgetMeta.dirty) {
                    propertiesDiff(current.properties, next.properties, () => {
                        widgetMeta.dirty = true;
                    }, widgetMeta.customDiffProperties ? [...widgetMeta.customDiffProperties.values()] : []);
                }
                if (widgetMeta.dirty) {
                    _idToChildrenWrappers.delete(id);
                    didRender = true;
                    rendered = Constructor(createWidgetOptions(id, id, widgetMeta.middleware));
                    widgetMeta.dirty = false;
                    if (widgetMeta.deferRefs > 0) {
                        rendered = null;
                    }
                }
                widgetMeta.rendering = false;
                widgetMeta.propertiesCalled = false;
            }
        }
        else {
            const instanceData = widgetInstanceMap.get(instance);
            next.instance = instance;
            instanceData.rendering = true;
            instance.__setProperties__(next.node.properties);
            instance.__setChildren__(next.node.children);
            if (instanceData.dirty) {
                didRender = true;
                _idToChildrenWrappers.delete(id);
                rendered = instance.__render__();
            }
            instanceData.rendering = false;
        }
        _idToWrapperMap.set(next.id, next);
        processResult.widget = { type: 'attach', instance, id, attached: false };
        let children;
        if (rendered) {
            rendered = Array.isArray(rendered) ? rendered : [rendered];
            children = renderedToWrapper(rendered, next, current);
            _idToChildrenWrappers.set(id, children);
        }
        if (didRender) {
            processResult.item = {
                current: currentChildren,
                next: children,
                meta: {}
            };
        }
        return processResult;
    }
    function _removeWidget({ current }) {
        current = getWNodeWrapper(current.id) || current;
        _idToWrapperMap.delete(current.id);
        const meta = widgetMetaMap.get(current.id);
        let currentChildren = _idToChildrenWrappers.get(current.id);
        _wrapperSiblingMap.delete(current);
        let processResult = {};
        if (!current.reparent) {
            _idToChildrenWrappers.delete(current.id);
            processResult = {
                item: {
                    current: currentChildren,
                    meta: {}
                }
            };
        }
        if (meta) {
            meta.registryHandler && meta.registryHandler.destroy();
            destroyHandles(meta);
            widgetMetaMap.delete(current.id);
        }
        else {
            processResult.widget = { type: 'detach', current, instance: current.instance };
        }
        return processResult;
    }
    function findDomNodeOnParentWrapper(id) {
        const children = _idToChildrenWrappers.get(id) || [];
        for (let i = 0; i < children.length; i++) {
            const child = children[i];
            if (child.domNode) {
                return child.id;
            }
            const childId = findDomNodeOnParentWrapper(child.id);
            if (childId) {
                return childId;
            }
        }
    }
    function _createDom({ next }) {
        const parentDomNode = findParentDomNode(next);
        const isVirtual = isVirtualWrapper(next);
        const isBody = isBodyWrapper(next);
        const isHead = isHeadWrapper(next);
        let mergeNodes = [];
        next.id = `${wrapperId++}`;
        _idToWrapperMap.set(next.id, next);
        if (!next.domNode) {
            if (next.node.domNode) {
                next.domNode = next.node.domNode;
            }
            else {
                if (next.node.tag === 'svg') {
                    next.namespace = NAMESPACE_SVG;
                }
                if (isBody) {
                    next.domNode = _mountOptions.nodeApi.getBody();
                }
                else if (isHead) {
                    next.domNode = _mountOptions.nodeApi.getHead();
                }
                else if (next.node.tag && !isVirtual) {
                    if (next.namespace) {
                        next.domNode = _mountOptions.nodeApi.createWithNamespace(next.namespace, next.node.tag);
                    }
                    else {
                        next.domNode = _mountOptions.nodeApi.create(next.node.tag);
                    }
                }
                else if (next.node.text != null) {
                    next.domNode = _mountOptions.nodeApi.createText(next.node.text);
                }
            }
            if (_insertBeforeMap && _allMergedNodes.length) {
                if (parentDomNode === _allMergedNodes[0].parentNode) {
                    _insertBeforeMap.set(next, _allMergedNodes[0]);
                }
            }
            if (isVirtualWrapper(next)) {
                mergeNodes = next.mergeNodes || [];
            }
        }
        else if (_mountOptions.merge) {
            next.merged = true;
            if (_mountOptions.nodeApi.isTextNode(next.domNode)) {
                if (_mountOptions.nodeApi.getProperty(next.domNode, 'data') !== next.node.text) {
                    _allMergedNodes = [next.domNode, ..._allMergedNodes];
                    next.domNode = global.document.createTextNode(next.node.text);
                    next.merged = false;
                }
            }
            else {
                mergeNodes = arrayFrom(_mountOptions.nodeApi.getChildren(next.domNode));
                _allMergedNodes = [..._allMergedNodes, ...mergeNodes];
            }
        }
        let children;
        if (next.domNode || isVirtual) {
            if (next.node.children && next.node.children.length) {
                children = renderedToWrapper(next.node.children, next, null);
                _idToChildrenWrappers.set(next.id, children);
            }
        }
        const dom = isSpecialWrapper(next)
            ? undefined
            : {
                next: next,
                parentDomNode: parentDomNode,
                type: 'create'
            };
        let currentChildren = undefined;
        if (next.reparent) {
            currentChildren = _idToChildrenWrappers.get(next.reparent);
            _idToChildrenWrappers.delete(next.reparent);
        }
        if (children || currentChildren) {
            return {
                item: {
                    current: currentChildren,
                    next: children,
                    meta: { mergeNodes }
                },
                dom,
                widget: isVirtual ? { type: 'attach', id: next.id, attached: false } : undefined
            };
        }
        return { dom };
    }
    function _updateDom({ current, next }) {
        next.domNode = current.domNode;
        next.namespace = current.namespace;
        next.id = current.id;
        next.childDomWrapperId = current.childDomWrapperId;
        let children;
        let currentChildren = _idToChildrenWrappers.get(next.id);
        if (next.node.text != null && next.node.text !== current.node.text) {
            next.domNode = _mountOptions.nodeApi.createText(next.node.text);
        }
        else if (next.node.children) {
            children = renderedToWrapper(next.node.children, next, current);
            _idToChildrenWrappers.set(next.id, children);
        }
        _wrapperSiblingMap.delete(current);
        _idToWrapperMap.set(next.id, next);
        return {
            item: {
                current: currentChildren,
                next: children,
                meta: {}
            },
            dom: { type: 'update', next, current }
        };
    }
    function _removeDom({ current }) {
        if (current.reparent) {
            return {};
        }
        const isSpecial = isSpecialWrapper(current);
        const children = _idToChildrenWrappers.get(current.id);
        _idToChildrenWrappers.delete(current.id);
        _idToWrapperMap.delete(current.id);
        _wrapperSiblingMap.delete(current);
        if (current.node.properties.key) {
            const widgetMeta = widgetMetaMap.get(current.owningId);
            const parentWrapper = getWNodeWrapper(current.owningId);
            if (widgetMeta) {
                widgetMeta.nodeMap && widgetMeta.nodeMap.delete(current.node.properties.key);
            }
            else if (parentWrapper && parentWrapper.instance) {
                const instanceData = widgetInstanceMap.get(parentWrapper.instance);
                instanceData && instanceData.nodeHandler.remove(current.node.properties.key);
            }
        }
        if (current.hasAnimations || isSpecial) {
            return {
                item: { current: children, meta: {} },
                dom: isSpecial ? undefined : { type: 'delete', current }
            };
        }
        if (children) {
            _deferredRenderCallbacks.push(() => {
                let wrappers = children || [];
                let wrapper;
                let specialIds = [];
                while ((wrapper = wrappers.pop())) {
                    if (isWNodeWrapper(wrapper)) {
                        wrapper = getWNodeWrapper(wrapper.id) || wrapper;
                        if (wrapper.instance) {
                            const instanceData = widgetInstanceMap.get(wrapper.instance);
                            instanceData && instanceData.onDetach();
                            wrapper.instance = undefined;
                        }
                        else {
                            const meta = widgetMetaMap.get(wrapper.id);
                            if (meta) {
                                meta.registryHandler && meta.registryHandler.destroy();
                                destroyHandles(meta);
                                widgetMetaMap.delete(wrapper.id);
                            }
                        }
                    }
                    let wrapperChildren = _idToChildrenWrappers.get(wrapper.id);
                    if (wrapperChildren) {
                        wrappers.push(...wrapperChildren);
                    }
                    if (isBodyWrapper(wrapper) || isHeadWrapper(wrapper)) {
                        specialIds.push(wrapper.id);
                    }
                    else if (specialIds.indexOf(wrapper.parentId) !== -1) {
                        if (isWNodeWrapper(wrapper) || isVirtualWrapper(wrapper)) {
                            specialIds.push(wrapper.id);
                        }
                        else if (wrapper.domNode && _mountOptions.nodeApi.getParent(wrapper.domNode)) {
                            const parent = _mountOptions.nodeApi.getParent(wrapper.domNode);
                            if (parent) {
                                _mountOptions.nodeApi.removeChild(parent, wrapper.domNode);
                            }
                        }
                    }
                    else if (isDomVNode(wrapper.node) && wrapper.node.onDetach) {
                        wrapper.node.onDetach();
                    }
                    _idToChildrenWrappers.delete(wrapper.id);
                    _idToWrapperMap.delete(wrapper.id);
                }
            });
        }
        return {
            dom: { type: 'delete', current }
        };
    }
    return {
        mount,
        unmount,
        invalidate
    };
}
export default renderer;
//# sourceMappingURL=vdom.mjs.map